/**
 * Created by Claude on 2026-07-07
 * Rebuildable - a component that rebuilds its node's prefab children in place.
 *
 * rebuild() tears down and re-instantiates the node's children (re-evaluating each child's "if"
 * predicate), but keeps the node and its attached components (property source / controller / this).
 * That refreshes the structurally-flipping regions (a tile's limit "N left" vs cooldown, the
 * subscription / growth-fund / lives description states) the same way a full re-instantiate would - but
 * without swapping the node, so external references to it (the shop's tile list, scroll caches, the
 * close animation) stay valid and the node components' event subscriptions are not recreated.
 *
 * The build context captured at construction carries the node's config and property sources - the
 * components that survive a rebuild are exactly the ones registered there - so this works on any
 * prefab node, not only the root.
 *
 * rebuildChild(name) does the same for a single named region, for content that must not be torn down
 * wholesale.
 */

class Rebuildable extends BaseComponent {
    rebuild() {
        const node = this.node;
        const context = this.context;

        // removeAllChildren(true) runs each child's cleanup -> ComponentMap.detachAll, so old child
        // components (countdown views etc.) are disposed before the fresh children are built.
        node.removeAllChildren(true);

        PrefabBuilder.createChildren(context.config, context, node);

        // Replay the tile lifecycle over the whole prefab: the fresh children get onReady/onResize, and the
        // persistent root controller re-caches its node references (button / icon / ...) and refreshes
        // availability via its own re-fired onReady. onAttach is not re-fired - root components stay attached -
        // so event subscriptions are not duplicated.
        cc.Base.PerformRecursiveFromLeaves(node, (child) => {
            ComponentMap.callHooks(child, "onReady");
        });
        cc.Base.PerformRecursiveFromLeaves(node, (child) => {
            ComponentMap.callHooks(child, "onResize");
            if (child.onResize) {
                child.onResize();
            }
        });
        cc.Base.PerformRecursiveFromLeaves(node, (child) => {
            ComponentMap.callHooks(child, "onAfterResize");
            if (child.onAfterResize) {
                child.onAfterResize();
            }
        });
    }

    // A model event that changes one region alone does not need the whole content back - the prefab still
    // owns how that region is built, so the region is dropped and rebuilt from its own declaration. What
    // the rebuild leaves standing (a spine mid-animation, a node handed to another parent, a registered
    // target) is why a component reaches for this instead of rebuild().
    rebuildChild(name) {
        const node = this.node;
        const context = this.context;
        const config = context.config;

        const current = ComponentUtils.findChildByName(node, name);
        if (current) {
            current.removeFromParent();
        }

        const ownerConfig = Rebuildable.findOwnerConfig(config, name);
        const childConfig = ownerConfig.children.find((child) => child.name === name);

        if (!PrefabBuilder.checkCondition(childConfig, context)) {
            return undefined;
        }

        const child = PrefabBuilder.buildNode(childConfig, context);
        ComponentUtils.findChildByName(node, ownerConfig.name).addChild(child);
        return child;
    }
}

// The declaration a region hangs off - the node's own children and those of any named container
// inside it are both rebuilt this way.
Rebuildable.findOwnerConfig = function (config, name) {
    const children = config.children || [];

    for (let i = 0; i < children.length; i++) {
        if (children[i].name === name) {
            return config;
        }

        const owner = Rebuildable.findOwnerConfig(children[i], name);
        if (owner) {
            return owner;
        }
    }

    return undefined;
};

Prefab.registerComponent("Rebuildable", Rebuildable);
